import { useState } from "react";
import {
  Loader2, ArrowRight, ShieldCheck, CreditCard, Settings,
  AlertCircle, Mail, Phone, LifeBuoy, FileText,
} from "lucide-react";
import { api } from "../lib/api";
import { useContentText } from "../lib/content";
import { useSeo } from "../lib/useSeo";
import ReferAFriend from "../components/ReferAFriend";
import TrustStatement from "../components/TrustStatement";

/**
 * Customer account page.
 *
 * No bespoke customer auth - instead we use Stripe's hosted Customer Portal as
 * the secure surface: enter your email, we look up your Stripe customer_id,
 * Stripe generates a signed portal link, the customer redirects there to view
 * invoices, update card, cancel, etc. Stripe handles the actual identity step
 * on its own (email confirmation + login link) before granting portal access.
 */
export default function Account() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [referral, setReferral] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);

  useSeo({ title: "My account - Business Name Protection", noindex: true });

  const phone = useContentText("footer.contact.phone", "0800 069 9090");
  const phoneDigits = phone.replace(/\D/g, "");
  const supportEmail = useContentText("footer.contact.email", "info@nbrg.co.uk");

  const fetchReferral = async (value) => {
    setReferralLoading(true);
    try {
      const { data } = await api.post("/billing/referral-info", { email: value });
      setReferral(data);
    } catch {
      setReferral(null);
    } finally {
      setReferralLoading(false);
    }
  };

  const openPortal = async (e) => {
    e?.preventDefault?.();
    setError("");
    const value = email.trim().toLowerCase();
    if (!value || !value.includes("@")) {
      setError("Please enter the email address you used to subscribe.");
      return;
    }
    setLoading(true);
    // In parallel: fetch the referral code so it can be displayed if portal redirect is slow / fails
    fetchReferral(value);
    try {
      const r = await api.post("/billing/portal", { email: value, origin_url: window.location.origin });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        setError("Unexpected response. Please try again or contact our team.");
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setError("No active subscription found for that email. Double-check the address you paid with, or contact our team.");
      } else {
        setError("We could not open your account just now. Please try again in a moment.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="bg-[#f7f7f7] min-h-screen py-16 md:py-24 lg:py-28 xl:py-32" data-testid="account-page">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
        {/* ───────────── HEADER ───────────── */}
        <div className="text-center max-w-xl mx-auto mb-10">
          <span className="bnp-chip bnp-chip-light">Customer area</span>
          <h1 className="font-display text-[clamp(2rem,4vw,3.4rem)] text-[#04273d] mt-3 font-semibold tracking-tight leading-[1.08]">
            Access your account
          </h1>
          <p className="text-[#447694] mt-4 text-base md:text-lg leading-relaxed">
            Enter the email address you used when you signed up.
          </p>
          <p className="text-[#447694] mt-2 text-base md:text-lg leading-relaxed">
            We will send you a secure link to your subscription portal. No password is required.
          </p>
        </div>

        {/* ───────────── EMAIL FORM ───────────── */}
        <div className="bnp-card p-7 md:p-9" data-testid="account-card">
          <form onSubmit={openPortal} className="space-y-4">
            <div>
              <label htmlFor="account-email" className="block text-xs font-display font-semibold text-[#447694] tracking-widest uppercase mb-2">
                Email address
              </label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#447694]" />
                <input
                  id="account-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(""); }}
                  placeholder="you@yourbusiness.co.uk"
                  className="w-full pl-10 pr-4 py-3.5 rounded-full border-2 border-[#9ac7f7]/40 focus:border-[#04273d] focus:outline-none font-display text-[#04273d] text-base placeholder:text-[#9ac7f7]"
                  data-testid="account-email-input"
                  disabled={loading}
                />
              </div>
            </div>

            {error && (
              <div className="rounded-2xl bg-amber-50 border border-amber-300 p-4 flex items-start gap-3" data-testid="account-error">
                <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-sm text-[#04273d]">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center py-4 text-base disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="account-open-portal-button"
            >
              {loading ? (
                <><Loader2 size={16} className="animate-spin" /> Opening your account...</>
              ) : (
                <>Access my subscription <ArrowRight size={16} /></>
              )}
            </button>

            <p className="text-[11.5px] text-[#447694] text-center pt-1">
              Stripe will verify your email before granting access.
            </p>
          </form>
          <TrustStatement className="mt-5" />
        </div>

        {/* ───────────── REFER A FRIEND ───────────── */}
        {referralLoading && !referral && (
          <div className="mt-6 rounded-3xl bg-white border-2 border-[#9ac7f7]/30 p-8 text-center" data-testid="refer-a-friend-loading">
            <Loader2 size={20} className="animate-spin text-[#447694] mx-auto" />
            <p className="text-sm text-[#447694] mt-3">Loading your referral panel...</p>
          </div>
        )}
        <ReferAFriend referral={referral} />

        {/* ───────────── WHAT YOU CAN DO IN YOUR ACCOUNT ───────────── */}
        <section className="mt-12" data-testid="account-features-section">
          <div className="text-center max-w-2xl mx-auto mb-7">
            <h2 className="font-display text-2xl md:text-3xl text-[#04273d] font-bold tracking-tight">
              What you can do in your account
            </h2>
            <p className="text-[#447694] mt-3 text-sm md:text-base leading-relaxed">
              Once inside your secure portal, you can:
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bnp-card p-6 flex flex-col" data-testid="account-feature-billing">
              <div className="h-11 w-11 rounded-xl bg-[#71edc9]/25 flex items-center justify-center">
                <CreditCard size={20} className="text-[#04273d]" />
              </div>
              <h3 className="font-display text-lg text-[#04273d] mt-4 font-bold">Manage payments</h3>
              <p className="text-sm text-[#447694] mt-2 leading-relaxed">
                Update your card details or add a backup payment method. Any changes will apply to your next billing cycle.
              </p>
            </div>
            <div className="bnp-card p-6 flex flex-col" data-testid="account-feature-invoices">
              <div className="h-11 w-11 rounded-xl bg-[#71edc9]/25 flex items-center justify-center">
                <FileText size={20} className="text-[#04273d]" />
              </div>
              <h3 className="font-display text-lg text-[#04273d] mt-4 font-bold">View invoices</h3>
              <p className="text-sm text-[#447694] mt-2 leading-relaxed">
                Download VAT-compliant invoices for your records or accountant.
              </p>
            </div>
            <div className="bnp-card p-6 flex flex-col" data-testid="account-feature-manage">
              <div className="h-11 w-11 rounded-xl bg-[#71edc9]/25 flex items-center justify-center">
                <Settings size={20} className="text-[#04273d]" />
              </div>
              <h3 className="font-display text-lg text-[#04273d] mt-4 font-bold">Update or cancel your plan</h3>
              <p className="text-sm text-[#447694] mt-2 leading-relaxed">
                Manage and cancel your subscription whenever you need to.
              </p>
            </div>
          </div>
        </section>

        {/* ───────────── NEED HELP? ───────────── */}
        <section className="mt-12" data-testid="account-help-section">
          <div className="bnp-card p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-2xl bg-[#04273d] flex items-center justify-center shrink-0">
                <LifeBuoy size={20} className="text-[#71edc9]" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-display text-xl md:text-2xl text-[#04273d] font-bold tracking-tight">
                  Need help?
                </h2>
                <p className="text-[#447694] mt-2 text-sm md:text-base leading-relaxed">
                  If you need support with your account or subscription, our UK team is here to help.
                </p>
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  <a
                    href={`mailto:${supportEmail}`}
                    className="flex items-center gap-3 rounded-2xl border-2 border-[#9ac7f7]/30 px-4 py-3 hover:border-[#04273d] transition-colors"
                    data-testid="account-help-email"
                  >
                    <Mail size={16} className="text-[#0d9b80] shrink-0" />
                    <span className="font-display text-[#04273d] font-semibold text-sm md:text-base break-all">{supportEmail}</span>
                  </a>
                  <a
                    href={`tel:${phoneDigits}`}
                    className="flex items-center gap-3 rounded-2xl border-2 border-[#9ac7f7]/30 px-4 py-3 hover:border-[#04273d] transition-colors"
                    data-testid="account-help-phone"
                  >
                    <Phone size={16} className="text-[#0d9b80] shrink-0" />
                    <span className="font-display text-[#04273d] font-semibold text-sm md:text-base">{phone}</span>
                  </a>
                </div>
                <p className="text-xs text-[#447694] mt-4">
                  We respond within one working day.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ───────────── ABOUT YOUR SUBSCRIPTION ───────────── */}
        <section className="mt-12" data-testid="account-about-section">
          <div className="bnp-card p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-2xl bg-[#71edc9]/25 flex items-center justify-center shrink-0">
                <ShieldCheck size={20} className="text-[#04273d]" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-display text-xl md:text-2xl text-[#04273d] font-bold tracking-tight">
                  About your subscription
                </h2>
                <p className="text-[#447694] mt-3 text-sm md:text-base leading-relaxed">
                  Business Name Protection helps you maintain a structured record of your business name and access support if a naming issue arises.
                </p>
                <p className="text-[#447694] mt-2 text-sm md:text-base leading-relaxed">
                  If you ever have questions about your cover or whether it is still right for your business, our team can review it with you.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
